import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../ui/icon'

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

export const SettingsModal = ({ isOpen, onClose }: SettingsModalProps) => {
  const { t, i18n } = useTranslation()
  const [theme, setTheme] = useState(() => localStorage.getItem('opakor_theme') || 'dark')

  const handleSetTheme = useCallback((newTheme: string) => {
    setTheme(newTheme)
    localStorage.setItem('opakor_theme', newTheme)
    document.documentElement.setAttribute('data-theme', newTheme)
  }, [])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-bg-surface rounded-2xl shadow-2xl border border-border-default w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-default">
          <h2 className="text-lg font-semibold text-fg-primary">{t('settings.title')}</h2>
          <button onClick={onClose} className="rounded-full p-1.5 text-fg-muted hover:text-fg-primary hover:bg-interactive-hover transition-colors">
            <Icon icon="x" size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          <section>
            <h3 className="text-sm font-semibold text-fg-primary mb-3">{t('settings.language')}</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => i18n.changeLanguage('en')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  i18n.language.startsWith('en') ? 'bg-accent text-white' : 'bg-bg-surface-alt text-fg-secondary hover:bg-interactive-hover'
                }`}
              >
                English
              </button>
              <button
                onClick={() => i18n.changeLanguage('km')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  i18n.language.startsWith('km') ? 'bg-accent text-white' : 'bg-bg-surface-alt text-fg-secondary hover:bg-interactive-hover'
                }`}
              >
                ភាសាខ្មែរ
              </button>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-fg-primary mb-3">{t('settings.theme')}</h3>
            <div className="flex items-center gap-2">
              {(['dark', 'blue', 'light'] as const).map((th) => (
                <button
                  key={th}
                  onClick={() => handleSetTheme(th)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    theme === th ? 'bg-accent text-white' : 'bg-bg-surface-alt text-fg-secondary hover:bg-interactive-hover'
                  }`}
                >
                  {th === 'dark' ? t('settings.themeDark') : th === 'blue' ? t('settings.themeBlue') : t('settings.themeLight')}
                </button>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-fg-primary mb-3">{t('settings.annotationDefaults')}</h3>
            <p className="text-xs text-fg-muted">{t('settings.annotationDefaultsHint')}</p>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-fg-primary mb-3">{t('dashboard.feedbackTitle')}</h3>
            <p className="text-xs text-fg-muted mb-2">{t('dashboard.feedbackDescription')}</p>
            <a
              href="https://forms.gle/p2WSd5rRojkwU3ce8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors"
            >
              {t('dashboard.feedbackButton')}
            </a>
          </section>

        </div>
      </div>
    </div>
  )
}
